"use client"

import { useQuery } from "@tanstack/react-query"
import { toDictionary } from "@tokenterminal/core/utils/toDictionary"
import { type RouterInputs, trpcClient } from "@tokenterminal/core/utils/trpc"

// Column configuration for the screener preview
// Based on the full screener table columns: fees and revenue with 7d, 30d, 180d intervals
const SCREENER_COLUMNS: RouterInputs["projects"]["postScreenerTable"]["columns"] =
  [
    { name: "name" },
    { name: "market_sectors" },
    { name: "chains" },
    { metric: "fees", interval: "7d", aggregation: "sum" },
    { metric: "fees", interval: "30d", aggregation: "sum" },
    { metric: "fees", interval: "180d", aggregation: "sum" },
    { metric: "fees", interval: "7d", aggregation: "trend" },
    { metric: "fees", interval: "30d", aggregation: "trend" },
    { metric: "fees", interval: "180d", aggregation: "trend" },
    { metric: "revenue", interval: "7d", aggregation: "sum" },
    { metric: "revenue", interval: "30d", aggregation: "sum" },
    { metric: "revenue", interval: "180d", aggregation: "sum" },
    { metric: "revenue", interval: "7d", aggregation: "trend" },
  ]

interface ScreenerRow {
  name: string
  slug: string
  logo: string
  symbol?: string
  market_sectors?: string[] // Array of market sector slugs/ids
  chains?: string[]
  fees_7d_sum?: number
  fees_30d_sum?: number
  fees_180d_sum?: number
  fees_7d_trend?: number
  fees_30d_trend?: number
  fees_180d_trend?: number
  revenue_7d_sum?: number
  revenue_30d_sum?: number
  revenue_180d_sum?: number
  revenue_7d_trend?: number
}

interface ChainMetadata {
  chain_id: string
  name: string
  logo: string
}

interface MarketSectorMetadata {
  id: string
  name: string
  show_dashboard?: boolean
}

export function useScreenerTableData(limit: number = 15) {
  // Fetch screener data
  const {
    data: screenerData,
    isLoading: isScreenerLoading,
    error: screenerError,
  } = useQuery({
    queryKey: ["screener-preview", limit],
    queryFn: async () => {
      const result = await trpcClient.projects.postScreenerTable.mutate({
        columns: SCREENER_COLUMNS,
        sorting: {
          column: "fees_7d_sum",
          desc: true,
        },
        limit,
        offset: 0,
      })
      return result.data as ScreenerRow[]
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  })

  // Fetch chains metadata for logo display
  const { data: chainsData, isLoading: isChainsLoading } = useQuery({
    queryKey: ["chains-metadata"],
    queryFn: async () => {
      const result = await trpcClient.chains.getChains.query({
        include_all_chains: true,
      })
      return result.data as ChainMetadata[]
    },
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
  })

  // Fetch market sectors metadata for name display
  const { data: marketSectorsData, isLoading: isMarketSectorsLoading } =
    useQuery({
      queryKey: ["market-sectors-metadata"],
      queryFn: async () => {
        const result = await trpcClient.marketSectors.getMarketSectors.query()
        return result.data as MarketSectorMetadata[]
      },
      staleTime: 1000 * 60 * 60, // 1 hour
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
    })

  // Create chains lookup map
  const chainsById = chainsData
    ? toDictionary(chainsData, "chain_id")
    : new Map<string, ChainMetadata>()

  // Create market sectors lookup map
  const marketSectorsById = marketSectorsData
    ? toDictionary(marketSectorsData, "id")
    : new Map<string, MarketSectorMetadata>()

  return {
    data: screenerData ?? [],
    chainsById,
    marketSectorsById,
    isLoading: isScreenerLoading || isChainsLoading || isMarketSectorsLoading,
    error: screenerError,
  }
}
